"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EntanglementProtocolWriter = exports.EntanglementProtocolReader = exports.EntanglementProtocol = void 0;
const __1 = require("../..");
class EntanglementProtocol extends __1.AProtocol {
    constructor() {
        super();
    }
    read(data, handler) {
        const reader = new EntanglementProtocolReader(data);
        return handler(reader);
    }
    write(handler) {
        const writer = new EntanglementProtocolWriter();
        handler(writer);
        return writer.getContents();
    }
    async writeData(transport, metadata, handler) {
        const writer = new EntanglementProtocolWriter();
        handler(writer);
        await transport.send(new __1.TransportMessage(writer.getContents(), __1.VolatileTransportMetadata.fromMetadata(metadata)));
    }
    async writeTaggedData(transport, messageId, metadata, handler) {
        const writer = new EntanglementProtocolWriter();
        handler(writer);
        await transport.sendTagged(messageId, new __1.TransportMessage(writer.getContents(), __1.VolatileTransportMetadata.fromMetadata(metadata)));
    }
    async readData(transport, handler) {
        const receivedData = await transport.receive();
        const reader = new EntanglementProtocolReader(receivedData.payload);
        return handler(reader, receivedData.metadata);
    }
    async readTaggedData(transport, messageId, handler) {
        const receivedData = await transport.receiveTagged(messageId);
        const reader = new EntanglementProtocolReader(receivedData.payload);
        return handler(reader, receivedData.metadata);
    }
    async readBufferedTaggedData(transport, handler) {
        const { id, message } = await transport.receiveBufferedTagged();
        const reader = new EntanglementProtocolReader(message.payload);
        return handler(reader, id, message.metadata);
    }
    async writeAndReadData(transport, metadata, handler) {
        const writer = new EntanglementProtocolWriter();
        handler(writer);
        const receiveHandler = await transport.sendAndReceive(new __1.TransportMessage(writer.getContents(), __1.VolatileTransportMetadata.fromMetadata(metadata)));
        return async (readHandler) => {
            const { payload: data, metadata } = await receiveHandler();
            const reader = new EntanglementProtocolReader(data);
            return readHandler(reader, metadata);
        };
    }
}
exports.EntanglementProtocol = EntanglementProtocol;
class EntanglementProtocolReader extends __1.AProtocolReader {
    constructor(buffer) {
        super();
        this.buffer = buffer;
        this.position = 0;
    }
    readData() {
        const dataLength = this.buffer.readInt32LE(this.position);
        this.position += 4;
        return Buffer.from(this.buffer.slice(this.position, this.position += dataLength));
    }
    readStringValue() {
        const contentLength = this.buffer.readInt32LE(this.position);
        this.position += 4;
        if (contentLength > 0) {
            return this.buffer.toString('utf8', this.position, this.position += contentLength);
        }
        else {
            return '';
        }
    }
    readBooleanValue() {
        const value = this.buffer.readIntLE(this.position, 1) === 1;
        this.position += 1;
        return value;
    }
    readByteValue() {
        const value = this.buffer.readInt8(this.position);
        this.position += 1;
        return value;
    }
    readShortValue() {
        const value = this.buffer.readInt16LE(this.position);
        this.position += 2;
        return value;
    }
    readIntegerValue() {
        const value = this.buffer.readInt32LE(this.position);
        this.position += 4;
        return value;
    }
    readLongValue() {
        const reversedBuffer = this.buffer.slice(this.position, this.position + 7).reverse();
        this.position += 8;
        const hex = reversedBuffer.toString('hex');
        if (hex.length === 0)
            return BigInt(0);
        else
            return BigInt(`0x${hex}`);
    }
    readFloatValue() {
        const value = this.buffer.readFloatLE(this.position);
        this.position += 4;
        return value;
    }
    readDoubleValue() {
        const value = this.buffer.readDoubleLE(this.position);
        this.position += 8;
        return value;
    }
    readEnumValue() {
        const value = this.buffer.readInt32LE(this.position);
        this.position += 4;
        return value;
    }
}
exports.EntanglementProtocolReader = EntanglementProtocolReader;
class EntanglementProtocolWriter extends __1.AProtocolWriter {
    constructor() {
        super();
        this.buffer = Buffer.allocUnsafe(10 * 1024);
        this.position = 0;
    }
    getContents() {
        const trimmedBuffer = Buffer.allocUnsafe(this.position + 1);
        this.buffer.copy(trimmedBuffer, 0, 0, this.position);
        return trimmedBuffer;
    }
    checkPosition(length) {
        while (this.position + length > this.buffer.length) {
            const oldBuffer = this.buffer;
            this.buffer = Buffer.allocUnsafe(this.buffer.length * 2);
            oldBuffer.copy(this.buffer, 0, 0, oldBuffer.length);
        }
    }
    writeData(value) {
        this.checkPosition(4);
        this.buffer.writeInt32LE(value.length, this.position);
        this.position += 4;
        this.checkPosition(value.length);
        value.copy(this.buffer, this.position, 0, value.length);
        this.position += value.length;
    }
    writeStringValue(value) {
        const contentLength = Buffer.byteLength(value, 'utf8');
        this.checkPosition(4);
        this.buffer.writeInt32LE(contentLength, this.position);
        this.position += 4;
        this.checkPosition(contentLength);
        this.buffer.write(value, this.position, 'utf8');
        this.position += contentLength;
    }
    writeBooleanValue(value) {
        this.checkPosition(1);
        this.buffer.writeIntLE(value ? 1 : 0, this.position, 1);
        this.position += 1;
    }
    writeByteValue(value) {
        this.checkPosition(1);
        this.buffer.writeInt8(value, this.position);
        this.position += 1;
    }
    writeShortValue(value) {
        this.checkPosition(2);
        this.buffer.writeInt16LE(value, this.position);
        this.position += 2;
    }
    writeIntegerValue(value) {
        this.checkPosition(4);
        this.buffer.writeInt32LE(value, this.position);
        this.position += 4;
    }
    writeLongValue(value) {
        const hex = value.toString(16);
        const contentLength = Buffer.byteLength(hex.padStart(8 * 2, '0').slice(0, 8 * 2), 'hex');
        this.checkPosition(contentLength);
        this.buffer.write(hex, this.position, 'hex');
        this.position += contentLength;
    }
    writeFloatValue(value) {
        this.checkPosition(4);
        this.buffer.writeFloatLE(value, this.position);
        this.position += 4;
    }
    writeDoubleValue(value) {
        this.checkPosition(8);
        this.buffer.writeDoubleLE(value, this.position);
        this.position += 8;
    }
    writeEnumValue(value) {
        this.checkPosition(4);
        this.buffer.writeInt32LE(value, this.position);
        this.position += 4;
    }
}
exports.EntanglementProtocolWriter = EntanglementProtocolWriter;
